/**
 * The trial CTA pair for marketing surfaces.
 *
 * What: "Book a Free Trial" (opens the trial dialog) beside "Try the Practice
 * Demo", as pushable pills, owning the dialog's open state.
 * Why: The same pair — buttons, labels, dialog wiring, and the `data-inline-cta`
 * / `data-boot-cta` hooks the floating CTA and lean boot key on — was rebuilt in
 * every hero and CTA band. One primitive keeps the site's primary conversion
 * path identical everywhere.
 * When: Update when the trial entry path, CTA labeling, or button mechanics change.
 *
 * Hydration note: these routes are prerendered, so nothing here may branch on
 * viewport or motion preference in JS. Responsive and reduced-motion behaviour
 * resolves in CSS media queries only (see `landingButtons.ts`).
 */

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import {
  pushablePrimarySx,
  pushableSecondarySx,
} from '@/components/landing/landingButtons';
import ProUpsellDialog from '@/components/Practice/ProUpsellDialog';
import { useTuitionSubject } from '@/contexts/tuitionSubjectContext';

const DEFAULT_SECONDARY_LABEL = 'Try the Practice Demo';

interface LandingTrialCtasProps {
  /** Attribution tag carried into the trial lead (CAPI + reporting). */
  sourceLocation: string;
  align?: 'start' | 'center';
  /** Stretch both buttons edge-to-edge below `sm`. */
  fullWidthOnMobile?: boolean;
  secondaryTo?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  /**
   * Drop the demo link and render the trial button alone.
   *
   * For slim mid-page bands only, where the demo is a second way off a page the
   * reader has not finished. The hero and the closing CTA always show both.
   */
  showSecondary?: boolean;
  /** Layout-only overrides from the host section (margins, positioning). */
  sx?: SxProps<Theme>;
}

function LandingTrialCtas({
  sourceLocation,
  align = 'start',
  fullWidthOnMobile = true,
  secondaryTo = '/demo/practice',
  primaryLabel,
  secondaryLabel = DEFAULT_SECONDARY_LABEL,
  showSecondary = true,
  sx = undefined,
}: LandingTrialCtasProps) {
  const tuition = useTuitionSubject();
  const resolvedPrimaryLabel = primaryLabel ?? tuition.primaryCta;
  const [upsellOpen, setUpsellOpen] = useState(false);

  const widthSx = fullWidthOnMobile
    ? { width: { xs: '100%', sm: 'auto' } }
    : null;

  return (
    <>
      {/*
        `useFlexGap` + `flexWrap` is what keeps the row honest in a narrow host
        (the blog article's 42rem reading column at `sm`): the pair drops to a
        second line rather than squeezing until both labels break mid-phrase.
        Stack's default margin-based spacing does not survive a wrap, hence the
        flex gap. `stretch` keeps the two pills the same height when a label
        does wrap.
      */}
      <Stack
        data-inline-cta
        useFlexGap
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          flexWrap: 'wrap',
          justifyContent: align === 'center' ? 'center' : 'flex-start',
          alignItems: 'stretch',
          ...sx,
        }}
      >
        <Button
          onClick={() => setUpsellOpen(true)}
          data-boot-cta="trial"
          aria-label={resolvedPrimaryLabel}
          sx={{ ...pushablePrimarySx, ...widthSx }}
        >
          {resolvedPrimaryLabel}
        </Button>
        {showSecondary && tuition.publicDemo && (
          <Button
            component={RouterLink}
            to={secondaryTo}
            aria-label={secondaryLabel}
            sx={{ ...pushableSecondarySx, ...widthSx }}
          >
            {secondaryLabel}
          </Button>
        )}
      </Stack>
      <ProUpsellDialog
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        skipOverview={true}
        sourceLocation={sourceLocation}
      />
    </>
  );
}

export default LandingTrialCtas;
